import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const DMCAPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-900" data-testid="dmca-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-md border-b border-gray-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-gray-300 hover:text-white hover:bg-gray-800"
              data-testid="back-home-btn"
            >
              ← Back to Home
            </Button>
            <div className="flex items-center space-x-3">
              <div className="text-2xl font-bold text-white">
                <span className="text-orange-500">X</span>videxa
              </div>
              <div className="text-orange-400 text-sm bg-orange-600/20 px-2 py-1 rounded border border-orange-500/50">
                Premium HD
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="animate-slideInUp">
          <Card className="glass-effect-dark">
            <CardHeader>
              <CardTitle className="text-3xl font-bold title-primary text-center mb-4">
                DMCA Copyright Policy
              </CardTitle>
              <div className="text-center text-gray-400">
                Megavideosee.com respects the intellectual property rights of others
              </div>
            </CardHeader>
            <CardContent className="space-y-8 text-gray-300 leading-relaxed">
              <section>
                <h2 className="text-xl font-semibold text-orange-400 mb-4">Copyright Compliance</h2>
                <p className="mb-4">
                  Megavideosee.com ("we," "us," or "our") respects the intellectual property rights of others and expects our users to do the same. 
                  We respond to notices of alleged copyright infringement that comply with the Digital Millennium Copyright Act ("DMCA").
                </p>
                <p>
                  If you believe that your copyrighted work has been copied in a way that constitutes copyright infringement and is accessible 
                  via our service, please notify us by providing our designated copyright agent with the information listed below.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-orange-400 mb-4">Filing a DMCA Notice</h2>
                <p className="mb-4">
                  To file a DMCA notice with us, please provide the following information:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>A physical or electronic signature of the copyright owner or authorized agent</li>
                  <li>Identification of the copyrighted work claimed to have been infringed</li>
                  <li>Identification of the material that is claimed to be infringing and information sufficient to locate the material</li>
                  <li>Your contact information (address, telephone number, email address)</li>
                  <li>A statement that you have a good faith belief that use of the material is not authorized</li>
                  <li>A statement that the information in the notification is accurate and that you are authorized to act on behalf of the copyright owner</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-orange-400 mb-4">Designated Agent</h2>
                <div className="bg-gray-800/50 p-6 rounded-lg border border-orange-500/20">
                  <p className="mb-2"><strong>DMCA Agent:</strong> Legal Department</p>
                  <p className="mb-2"><strong>Email:</strong> dmca@megavideosee.com</p>
                  <p className="mb-2"><strong>Address:</strong> Megavideosee Legal Department</p>
                  <p className="mb-2">DMCA Compliance Office</p>
                  <p>Please mark correspondence "DMCA Notice"</p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-orange-400 mb-4">Counter-Notification</h2>
                <p className="mb-4">
                  If you believe that your content was removed by mistake or misidentification, you may file a counter-notification. 
                  To file a counter-notification, please provide:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Your physical or electronic signature</li>
                  <li>Identification of the material that has been removed and the location where it appeared</li>
                  <li>A statement under penalty of perjury that you have a good faith belief the material was removed by mistake</li>
                  <li>Your name, address, telephone number, and consent to jurisdiction</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-orange-400 mb-4">Content Attribution</h2>
                <div className="disclaimer">
                  <p className="mb-4">
                    <strong>Important Notice:</strong> Megavideosee.com aggregates content from various sources across the internet. 
                    We provide attribution links to original sources whenever possible to respect content creators' rights.
                  </p>
                  <p>
                    When viewing content on our platform, users can click the attribution link below each video to visit 
                    the original source and support the content creator directly.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-orange-400 mb-4">Repeat Infringer Policy</h2>
                <p>
                  Megavideosee.com has adopted a policy of terminating, in appropriate circumstances, users who are deemed to be repeat infringers. 
                  We also reserve the right to terminate users who we believe are using our service to infringe copyrights.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-orange-400 mb-4">Good Faith Requirement</h2>
                <p className="mb-4">
                  Please note that submitting a false DMCA notice may result in liability for damages, including attorney fees. 
                  We recommend consulting with an attorney before filing a DMCA notice if you are unsure about your rights.
                </p>
                <div className="bg-orange-600/10 border-l-4 border-orange-500 p-4">
                  <p className="text-orange-400 font-medium">
                    We are committed to working with copyright holders to address their concerns promptly and fairly.
                  </p>
                </div>
              </section>

              <section className="text-center pt-8 border-t border-gray-700">
                <p className="text-gray-500 text-sm">
                  This policy was last updated on September 2025. We reserve the right to modify this policy at any time.
                </p>
              </section>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DMCAPage;